import cn from 'classnames'
import {FC, useMemo} from 'react'

import {IncludedPriceBenefits} from '@/components/common/benefits/IncludedPriceBenefits'
import {Text, TextVariant} from '@/components/common/content/Text'
import {FormSummaryCountryDetail} from '@/components/common/form/summary/common/FormSummaryCountryDetail'
import {FormSummaryItem} from '@/components/common/form/summary/common/layout/FormSummaryItem'
import {FormSummarySection} from '@/components/common/form/summary/common/layout/FormSummarySection'
import {FormSummary} from '@/components/common/form/summary/FormSummary'
import {FormSummaryPriceWrapper} from '@/components/common/form/summary/price/FormSummaryPriceWrapper'
import {usePreferences} from '@/components/context/Preferences'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {ICON_SIZE_MEDIUM} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {add} from '@/utils/pricing/decimals'
import {
  calculateTmOwnershipTransferAssignmentAgreementFee,
  calculateTmOwnershipTransferJurisdictionsPrice,
} from '@/utils/pricing/tm-ownership-transfer/calculations'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {PricingEstimationCardProps} from '../calculator/PricingCalculatorTable'
import {PricingTrademarksSelect} from '../calculator/PricingTrademarksSelect'
import {useAssignmentAgreement} from './context/AssignmentAgreementContext'
import {PricingAssignmentAgreementRadioButtons} from './PricingAssignmentAgreementRadioButtons'

export const PricingTrademarkOwnershipTransferEstimationCard: FC<PricingEstimationCardProps> = ({
  selectedJurisdictions,
  trademarksByJurisdiction,
  setTrademarksByJurisdiction,
}) => {
  const {t} = useTranslation()
  const {currency} = usePreferences()
  const {
    calculator: {title, vatNotIncludedNote, totalPriceTitle, countriesTitle},
    trademarkOwnershipTransfer: {
      benefitsList,
      calculator: {description, assignmentAgreementQuestion},
    },
  } = getPricingContent()
  const {hasAssignmentAgreement} = useAssignmentAgreement()

  const price = useMemo(
    () =>
      calculateTmOwnershipTransferJurisdictionsPrice(
        selectedJurisdictions,
        currency,
        trademarksByJurisdiction,
      ),
    [selectedJurisdictions, currency, trademarksByJurisdiction],
  )
  const assignmentAgreementFee = useMemo(
    () =>
      calculateTmOwnershipTransferAssignmentAgreementFee(
        currency,
        undefined,
        false,
        hasAssignmentAgreement,
      ),
    [hasAssignmentAgreement, currency],
  )
  const estimatedPrice = add(price.total.vatBase, assignmentAgreementFee?.total.vatBase ?? 0)
  const hasSelectedJurisdictions = selectedJurisdictions.length > 0
  const hasMultipleJurisdictions = selectedJurisdictions.length > 1

  return (
    <>
      <FormSummary
        title={t(title)}
        totalPrice={estimatedPrice}
        currency={currency}
        hasOverlay={false}
      >
        <div className="description desktop">
          <IncludedPriceBenefits
            description={description}
            benefitsList={benefitsList}
            color={theme.palette.text.light}
            disclaimer={null}
          />
        </div>
        <div className="desktop">
          <FormSummarySection label={t(assignmentAgreementQuestion)}>
            <PricingAssignmentAgreementRadioButtons size="small" />
          </FormSummarySection>
        </div>
        <div className={cn(`fee ${hasSelectedJurisdictions && 'divider'}`)}>
          <FormSummaryItem
            title={t(assignmentAgreementFee.descKey)}
            price={assignmentAgreementFee.total.vatBase}
            currency={currency}
          />
        </div>
        {hasSelectedJurisdictions && (
          <FormSummarySection label={t(countriesTitle)}>
            {price.jurisdictions.map(({jurisdiction, total, serviceFee, legalFee}, i) => {
              const numOfTrademarks = trademarksByJurisdiction[jurisdiction] ?? 1

              return (
                <FormSummaryCountryDetail
                  key={jurisdiction}
                  jurisdiction={jurisdiction}
                  totalPrice={total.vatBase}
                  serviceFee={serviceFee.total.vatBase}
                  administrativeFee={legalFee?.total.vatBase ?? 0}
                  trackedEvent={{
                    name: 'Price breakdown expanded',
                    parameters: {
                      country: jurisdiction,
                      tab: 'Trademark ownership transfer',
                      page: 'Pricing page',
                    },
                  }}
                  currency={currency}
                  isPriceTooltipVisible={false}
                  isExpanded
                  textColor={theme.palette.text.default}
                >
                  <div
                    className={cn(
                      'wrapper',
                      hasMultipleJurisdictions && i < price.jurisdictions.length - 1 && 'divider',
                    )}
                  >
                    <div className="input-wrapper">
                      <PricingTrademarksSelect
                        minSelectedTrademarks={1}
                        selectedTrademarks={numOfTrademarks}
                        setSelectedTrademarks={(trademarks) =>
                          setTrademarksByJurisdiction((prev) => ({
                            ...prev,
                            [jurisdiction]: trademarks,
                          }))
                        }
                        trackedEvent={{
                          name: 'Change number of trademarks (Pricing page)',
                          parameters: {
                            country: jurisdiction,
                            tab: 'Trademark ownership transfer',
                            page: 'Pricing page',
                          },
                        }}
                      />
                    </div>
                  </div>
                </FormSummaryCountryDetail>
              )
            })}
          </FormSummarySection>
        )}
        <FormSummaryPriceWrapper
          priceTitle={t(totalPriceTitle)}
          estimatedPrice={estimatedPrice}
          hasDiscountInput={false}
        />
        <div className="note">
          <Text variant={TextVariant.caption} element="p">
            {t(vatNotIncludedNote)}
          </Text>
        </div>
      </FormSummary>

      <style jsx>{`
        .note {
          margin-top: ${theme.spacing[2]};
          margin-bottom: ${theme.spacing[4]};
        }
        .wrapper {
          margin-top: ${theme.spacing[2.5]};
        }
        .divider {
          padding-bottom: ${theme.spacing[3]};
          margin-bottom: ${theme.spacing[2]};
          border-bottom: 1px solid ${theme.palette.gray[200]};
        }
        .description {
          padding-bottom: ${theme.spacing[3.5]};
          margin-bottom: ${theme.spacing[3.5]};
          border-bottom: 1px solid ${theme.palette.gray[200]};
        }
        .input-wrapper {
          padding: ${theme.spacing[2]};
          padding-left: ${theme.spacing[2.5]};
          margin-left: calc(${ICON_SIZE_MEDIUM} + ${theme.spacing[2]});
          background-color: ${theme.palette.gray[100]};
          border-radius: 6px;
        }
        .input-wrapper + .input-wrapper {
          margin-top: ${theme.spacing[2]};
        }
        .fee {
          margin-top: ${theme.spacing[3]};
          margin-bottom: ${theme.spacing[3.5]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .note {
            margin-bottom: 0;
          }
          .desktop {
            display: none;
          }
          .fee {
            margin-top: -${theme.spacing[3]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .fee {
            margin-top: -${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
